/**
 * Spreadsheet -> eBay bulk-upload CSV (Isaac, 2026-10-13): the pure rules
 * behind the "Excel import" page. First real use: ~100 Dremel products from a
 * warehouse stock sheet. Deliberately generic -- column mapping, pricing,
 * category rules and the eBay policy names are all inputs, so the next sheet
 * (another brand, another warehouse) needs configuration, not code.
 *
 * Output follows an eBay Seller Hub "Reports > Upload" template: the template's
 * own header row decides the columns; item specifics (`C:Brand`, ...) not in the
 * template are appended, which eBay accepts.
 */
import type { Sheet } from "./xlsx-read";

/* ---------- reading the stock sheet ---------- */

export interface ColumnMap {
  sku: number;
  title: number;
  qty: number;
  model: number;
  price: number;
  retail: number;
  upc: number;
  epid: number;
  photo: number;
  description: number;
}

export interface ImportItem {
  sku: string;
  title: string;
  model: string;
  qty: number;
  /** A ready eBay price from the sheet, if it has one. */
  price: number | null;
  /** A retail reference price; rules.pricePct of it becomes the price. */
  retail: number | null;
  upc: string;
  epid: string;
  /** One or more https URLs separated by "|". */
  photo: string;
  description: string;
}

const norm = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/** Best-guess column mapping from header names; -1 = not found. The user can correct it in the UI. */
export function guessColumns(headers: string[]): ColumnMap {
  const find = (...needles: RegExp[]) => {
    for (const re of needles) {
      const i = headers.findIndex((h) => re.test(norm(h)));
      if (i >= 0) return i;
    }
    return -1;
  };
  return {
    sku: find(/^sku$/, /\bsku\b/, /custom label/, /part number|part no/),
    title: find(/^title$/, /\btitle\b/, /\bname\b/, /description/),
    // "Finished Goods" is the sellable count in Ninjamo's warehouse export; In Process / Wholesale are not.
    qty: find(/finished goods/, /^qty$|^quantity$/, /available|on hand|sellable/, /total qty/),
    model: find(/^model$/, /\bmodel\b/, /\bmpn\b/),
    price: find(/^price$/, /list price|sale price|ebay price|start price/),
    retail: find(/retail/, /\bmsrp\b/),
    upc: find(/\bupc\b|\bean\b|\bgtin\b|barcode/),
    epid: find(/\bepid\b/),
    photo: find(/photo|image|picture/),
    description: find(/^description$|long description/),
  };
}

/** The first row that looks like a header: 3+ filled cells with data rows under it. */
export function findHeaderRow(rows: string[][]): number {
  for (let i = 0; i < Math.min(rows.length, 15); i++) {
    const filled = (rows[i] ?? []).filter((c) => c.trim() !== "").length;
    const next = (rows[i + 1] ?? []).filter((c) => c.trim() !== "").length;
    if (filled >= 3 && next >= 2) return i;
  }
  return 0;
}

const cell = (row: string[], i: number) => (i >= 0 ? (row[i] ?? "").trim() : "");
const num = (s: string): number | null => {
  const n = Number(s.replace(/[$,\s]/g, ""));
  return s.trim() !== "" && Number.isFinite(n) ? n : null;
};

export function readItems(rows: string[][], headerRow: number, map: ColumnMap): ImportItem[] {
  const out: ImportItem[] = [];
  for (const row of rows.slice(headerRow + 1)) {
    const sku = cell(row, map.sku);
    // Skip blank lines and export footers such as a "TOTAL" row.
    if (!sku || /^total$/i.test(sku)) continue;
    out.push({
      sku,
      title: cell(row, map.title),
      model: cell(row, map.model),
      qty: Math.max(0, Math.trunc(num(cell(row, map.qty)) ?? 0)),
      price: num(cell(row, map.price)),
      retail: num(cell(row, map.retail)),
      upc: cell(row, map.upc),
      epid: cell(row, map.epid),
      photo: cell(row, map.photo),
      description: cell(row, map.description),
    });
  }
  return out;
}

/* ---------- rules ---------- */

export interface CategoryRule {
  id: string;
  name: string;
  /** Regex source tested against the field below, case-insensitive. */
  test: string;
  field: "title" | "model";
  /** Item specifics this category requires that the sheet cannot always give. */
  aspects?: ("voltage" | "batteryIncluded")[];
  /** Low-confidence guess: the preview asks a human to confirm it. */
  review?: boolean;
}

export interface ImportRules {
  brand: string;
  titleMax: number;
  /** price = retail x pricePct, ending in .99 (never above it). */
  pricePct: number;
  /**
   * A product whose retail price is under this is not worth listing alone
   * (fees and shipping eat it), so it is listed as a pack instead. 0 = never pack.
   */
  minListingPrice: number;
  /** The pack's retail value should reach at least this. */
  packTargetPrice: number;
  /** Pack sizes to choose from, smallest sufficient one wins. */
  packSizes: number[];
  skipZeroQty: boolean;
  conditionId: string;
  format: string;
  duration: string;
  shippingProfile: string;
  returnProfile: string;
  paymentProfile: string;
  /** First match wins; `fallbackCategory` otherwise. */
  categories: CategoryRule[];
  fallbackCategory: { id: string; name: string };
  /** {title} {model} {brand} {pack} are substituted. */
  descriptionTemplate: string;
}

/**
 * Dremel rules, from the 2026-10-13 stock sheet; ordered, first match wins.
 * Categories were checked against where sellers actually list the same models
 * on eBay (Browse API, 30 new listings per model): blades mostly sit in 122838,
 * the Moto-Saw tool in Scroll Saws, the 12V battery in Power Tool Batteries,
 * the pet grooming kit in Pet Supplies > Claw Care, the Ultra-Saw in Circular
 * Saws. Only the sanding sheets and the Multi-Max tools were genuinely split,
 * so those stay flagged for a human.
 */
export const DREMEL_CATEGORIES: CategoryRule[] = [
  { id: "260173", name: "Scroll Saws", field: "model", test: "^MS20" },
  { id: "260204", name: "Power Tool Batteries", field: "model", test: "^B\\d{3}" },
  { id: "177793", name: "Claw Care", field: "model", test: "PGK$" },
  {
    id: "71307",
    name: "Circular Saws",
    field: "model",
    test: "^US40",
    aspects: ["voltage", "batteryIncluded"],
  },
  {
    id: "260167",
    name: "Oscillating Tools",
    field: "model",
    test: "^MM(35|50)-\\d",
    aspects: ["voltage", "batteryIncluded"],
    review: true,
  },
  {
    id: "260207",
    name: "Rotary Tool Accessories",
    field: "model",
    test: "^MM(14|70)",
    review: true,
  },
  { id: "122838", name: "Other Saw Blades", field: "model", test: "^MM\\d+[A-Z]*(-\\d+)?$" },
  {
    id: "177003",
    name: "Rotary Tools",
    field: "model",
    test: "^(2050|3100|4000|4250|4300|7760-N|8220|8240|8250)\\b",
    aspects: ["voltage", "batteryIncluded"],
  },
];

/** eBay business-policy IDs for the default policy names (from Seller Hub, 2026-10-13). */
export const DEFAULT_POLICY_IDS = {
  shipping: { "Top Rated Shipping": "152934738011" },
  returns: { "Top Rated Return": "229861826011" },
  payment: { "Payment Policy": "152835562011" },
} as const;

export const DEFAULT_RULES: ImportRules = {
  brand: "Dremel",
  titleMax: 80,
  pricePct: 0.85,
  minListingPrice: 15,
  packTargetPrice: 20,
  packSizes: [2, 3, 4, 5, 6, 8, 10, 12, 20, 25],
  skipZeroQty: true,
  conditionId: "1000-New",
  format: "FixedPrice",
  duration: "GTC",
  shippingProfile: "Top Rated Shipping",
  returnProfile: "Top Rated Return",
  paymentProfile: "Payment Policy",
  categories: DREMEL_CATEGORIES,
  fallbackCategory: { id: "260207", name: "Rotary Tool Accessories" },
  descriptionTemplate:
    "<p><b>{title}</b></p><ul><li>Brand: {brand}</li><li>Model: {model}</li><li>Number in pack: {pack}</li><li>Condition: New</li></ul>",
};

/* ---------- titles ---------- */

const DANGLING = /(?:\s+(?:for|with|and|or|of|in|on|to|a|an|the|by|w\/)|\s+[/+&-])$/i;

/** Drops the "..." cut-off and whatever phrase it left unfinished. */
function stripCutOff(t: string): string {
  let s = t.replace(/\s*(\.\.\.|…)\s*$/, "");
  if (s === t) return s;
  // The dots touch the last character (no space before them), so the export cut
  // the text mid-word ("... Car Pa..."): that last token is a fragment, drop it.
  if (!/\s(\.\.\.|…)\s*$/.test(t)) s = s.replace(/\s*\S+$/, "");
  // A parenthesis opened but never closed: the cut landed inside it.
  const open = s.lastIndexOf("(");
  if (open > s.lastIndexOf(")")) s = s.slice(0, open);
  s = s.trim();
  // "... with 5" -- a connector plus a number whose noun was cut off.
  s = s.replace(/\s+(?:for|with|and|or|of|in|on|to|by|w\/)\s+[\d./-]+$/i, "");
  while (DANGLING.test(s)) s = s.replace(DANGLING, "").trim();
  return s;
}

/** Number of pieces from "(5-Pack)", "(12-Piece)", "6-Piece", "2-Pack"; null if none. */
export function packCount(title: string): number | null {
  const m = /\(?\b(\d{1,3})[- ]?(?:pack|pk|piece|pc|pcs|pieces)\b\)?/i.exec(title);
  return m ? Number(m[1]) : null;
}

/** An eBay-sized title that still reads as a title: brand and model first, whole words only. */
export function compactTitle(raw: string, model: string, brand: string, max = 80): string {
  let t = stripCutOff(raw.replace(/\s+/g, " ").trim());
  // "DREMEL" -> "Dremel": all-caps brand reads as shouting and wastes nothing, but looks careless.
  t = t.replace(new RegExp(`\\b${brand.toUpperCase()}\\b`), brand);
  if (!t.toLowerCase().includes(brand.toLowerCase())) t = `${brand} ${t}`;
  if (model && !t.toLowerCase().includes(model.toLowerCase()))
    t = t.replace(brand, `${brand} ${model}`);
  const squeezes: [RegExp, string][] = [
    [/\s*\(\d{5,}\)/g, ""], // internal part-number codes like (103242)
    [/\s*\([^)]*\)\s*$/g, ""], // a trailing remark in parentheses
    [/\bRotary Tool\b/g, "Rotary Tool"],
    [/\s+(for|with)\s+[^,]*$/i, ""], // "... for Metal Wood and Plastic"
  ];
  for (const [re, to] of squeezes) {
    if (t.length <= max) break;
    const next = t.replace(re, to).trim();
    // Never squeeze away the model or the pack size.
    if (next.length >= 12 && (!model || next.toLowerCase().includes(model.toLowerCase()))) t = next;
  }
  if (t.length > max) {
    t = t.slice(0, max + 1);
    t = t.slice(0, Math.max(t.lastIndexOf(" "), 0)).trim();
    const open = t.lastIndexOf("(");
    if (open > t.lastIndexOf(")")) t = t.slice(0, open).trim();
    while (DANGLING.test(t)) t = t.replace(DANGLING, "").trim();
  }
  return t;
}

/* ---------- price ---------- */

/** Largest price ending in .99 that is not above `value` (105.38 -> 104.99). */
export function endIn99(value: number): number {
  if (value < 1.99) return Math.round(value * 100) / 100;
  return Math.round((Math.floor(value + 0.01) - 0.01) * 100) / 100;
}

export function priceFor(item: ImportItem, rules: ImportRules, pack = 1): number | null {
  if (item.price !== null) return pack === 1 ? item.price : endIn99(item.price * pack);
  if (item.retail !== null) return endIn99(item.retail * rules.pricePct * pack);
  return null;
}

/** Smallest configured pack size whose retail value reaches the target; 1 when the item is worth listing alone. */
export function packSizeFor(unitRetail: number | null, rules: ImportRules): number {
  if (!rules.minListingPrice || unitRetail === null || unitRetail >= rules.minListingPrice)
    return 1;
  const target = Math.max(rules.packTargetPrice, rules.minListingPrice);
  const sizes = [...rules.packSizes].sort((a, b) => a - b);
  return sizes.find((n) => unitRetail * n >= target) ?? sizes[sizes.length - 1] ?? 1;
}

/* ---------- building rows ---------- */

export type Flag =
  | "missingPrice"
  | "missingPhoto"
  | "zeroQty"
  | "duplicateSku"
  | "reviewCategory"
  | "titleCutOff"
  | "missingVoltage"
  | "tooLongSku"
  | "notEnoughForPack";

export interface Listing {
  sku: string;
  title: string;
  originalTitle: string;
  category: { id: string; name: string };
  /** Stock units this listing uses per sale (1 = single item, 6 = a lot of 6). */
  pack: number;
  qty: number;
  price: number | null;
  /** Item specifics: "C:Brand" -> value. */
  specifics: Record<string, string>;
  fields: Record<string, string>;
  flags: Flag[];
  /** Rows that cannot be uploaded as they are (no stock, duplicate SKU). */
  blocked: boolean;
}

function voltageOf(title: string): string {
  const m = /(\d+(?:\.\d+)?)\s?-?\s?(?:V\b|Volt)/i.exec(title);
  if (m) return `${m[1]} V`;
  return /corded|amp\b/i.test(title) ? "120 V" : "";
}

export function buildListings(items: ImportItem[], rules: ImportRules): Listing[] {
  const seen = new Map<string, number>();
  for (const it of items) seen.set(it.sku, (seen.get(it.sku) ?? 0) + 1);

  return items.map((it) => {
    const flags: Flag[] = [];
    const rule = rules.categories.find((r) =>
      new RegExp(r.test, "i").test(r.field === "model" ? it.model : it.title),
    );
    const category = rule ?? rules.fallbackCategory;
    if (rule?.review) flags.push("reviewCategory");

    // Cheap items go out as a lot: the pack size comes from the retail price, the
    // quantity is the stock divided by it, and the SKU says how many units it holds.
    const pack = packSizeFor(it.retail ?? it.price, rules);
    const lotSuffix = pack > 1 ? ` - Lot of ${pack}` : "";
    const sku = pack > 1 ? `${it.sku}-P${pack}` : it.sku;
    const qty = Math.floor(it.qty / pack);
    const title =
      compactTitle(it.title, it.model, rules.brand, rules.titleMax - lotSuffix.length) + lotSuffix;
    if (/(\.\.\.|…)\s*$/.test(it.title)) flags.push("titleCutOff");

    const price = priceFor(it, rules, pack);
    if (price === null) flags.push("missingPrice");
    if (!it.photo && !it.epid) flags.push("missingPhoto");
    if (sku.length > 50) flags.push("tooLongSku");
    if (it.qty <= 0) flags.push("zeroQty");
    else if (qty < 1) flags.push("notEnoughForPack");
    if ((seen.get(it.sku) ?? 0) > 1) flags.push("duplicateSku");

    const packCountInTitle = packCount(it.title);
    const specifics: Record<string, string> = {
      "C:Brand": rules.brand,
      "C:Model": it.model,
      "C:MPN": it.model,
    };
    if (packCountInTitle || pack > 1) {
      specifics["C:Number in Pack"] = String((packCountInTitle ?? 1) * pack);
    }
    if (rule?.aspects?.includes("voltage") || rule?.aspects?.includes("batteryIncluded")) {
      const voltage = voltageOf(it.title);
      if (!voltage) flags.push("missingVoltage");
      specifics["C:Voltage"] = voltage;
      specifics["C:Battery Included"] = /cordless|battery|li-?ion|lithium/i.test(it.title)
        ? "Yes"
        : "No";
    }

    const description =
      it.description ||
      rules.descriptionTemplate
        .replaceAll("{title}", title)
        .replaceAll("{model}", it.model)
        .replaceAll("{brand}", rules.brand)
        .replaceAll("{pack}", String((packCountInTitle ?? 1) * pack))
        .concat(pack > 1 ? `<p>Sold as a lot of ${pack} identical items.</p>` : "");

    return {
      sku,
      title,
      originalTitle: it.title,
      category: { id: category.id, name: category.name },
      pack,
      qty,
      price,
      specifics,
      fields: {
        upc: it.upc,
        epid: it.epid,
        photo: it.photo,
        description,
      },
      flags,
      blocked: (rules.skipZeroQty && it.qty <= 0) || qty < 1 || (seen.get(it.sku) ?? 0) > 1,
    };
  });
}

/* ---------- eBay template + CSV ---------- */

export interface EbayTemplate {
  /** The "#INFO" lines above the header, copied through to the CSV. */
  infoRows: string[][];
  headers: string[];
  /** Condition dropdown text for "New" as the template spells it, e.g. "1000-New". */
  conditionNew: string | null;
  /** Business policy names found in the template (ids stripped). */
  policies: { shipping: string[]; returns: string[]; payment: string[] };
  /** Same policies, name -> eBay policy ID (the API takes IDs, the CSV takes names). */
  policyIds: {
    shipping: Record<string, string>;
    returns: Record<string, string>;
    payment: Record<string, string>;
  };
  /** Category the template was generated for, if it says. */
  category: { name: string; id: string } | null;
}

const stripPolicyId = (s: string) => s.replace(/\s*-?\s*\(ID:\s*\d+\)\s*$/i, "").trim();
const policyIdOf = (s: string) => /\(ID:\s*(\d+)\)\s*$/i.exec(s)?.[1] ?? "";

export function parseEbayTemplate(sheets: Sheet[]): EbayTemplate {
  const listings = sheets.find((s) => s.name === "Listings");
  if (!listings) throw new Error('This does not look like an eBay template (no "Listings" sheet)');
  const headerIdx = listings.rows.findIndex((r) => (r[0] ?? "").startsWith("*Action"));
  if (headerIdx < 0)
    throw new Error("Could not find the header row (*Action...) in the Listings sheet");
  const trim = (r: string[]) => {
    const a = [...r];
    while (a.length && a[a.length - 1] === "") a.pop();
    return a;
  };
  const headers = trim(listings.rows[headerIdx] ?? []);
  const infoRows = listings.rows
    .slice(0, headerIdx)
    .filter((r) => (r[0] ?? "").startsWith("#"))
    .map(trim);

  const cats = sheets.find((s) => s.name === "Categories");
  const catRow = cats?.rows.find((r) => /^\d+$/.test((r[1] ?? "").trim()));
  const policySheet = sheets.find((s) => s.name === "BusinessPolicy");
  const col = (i: number) =>
    (policySheet?.rows.slice(1) ?? []).map((r) => stripPolicyId(r[i] ?? "")).filter(Boolean);
  const ids = (i: number) => {
    const m: Record<string, string> = {};
    for (const r of policySheet?.rows.slice(1) ?? []) {
      const id = policyIdOf(r[i] ?? "");
      if (id) m[stripPolicyId(r[i] ?? "")] = id;
    }
    return m;
  };

  return {
    infoRows,
    headers,
    conditionNew: (catRow ?? []).find((c) => /^1000\b/.test(c)) ?? null,
    policies: { shipping: col(0), returns: col(1), payment: col(2) },
    policyIds: { shipping: ids(0), returns: ids(1), payment: ids(2) },
    category: catRow ? { name: catRow[0] ?? "", id: (catRow[1] ?? "").trim() } : null,
  };
}

/** Value for a template header, or undefined to leave the cell empty. */
function valueFor(
  h: string,
  l: Listing,
  rules: ImportRules,
  template: EbayTemplate,
): string | undefined {
  const k = h.toLowerCase().trim();
  if (k.startsWith("*action")) return "Add";
  if (k === "custom label (sku)") return l.sku;
  if (k === "category id") return l.category.id;
  if (k === "category name") return l.category.name;
  if (k === "title") return l.title;
  if (k === "p:upc") return l.fields.upc || undefined;
  if (k === "p:epid") return l.fields.epid || undefined;
  if (k === "start price") return l.price === null ? undefined : l.price.toFixed(2);
  if (k === "quantity") return String(l.qty);
  if (k === "item photo url") return l.fields.photo || undefined;
  if (k === "condition id") return template.conditionNew ?? rules.conditionId;
  if (k === "description") return l.fields.description;
  if (k === "format") return rules.format;
  if (k === "duration") return rules.duration;
  if (k === "shipping profile name") return rules.shippingProfile;
  if (k === "return profile name") return rules.returnProfile;
  if (k === "payment profile name") return rules.paymentProfile;
  if (h.startsWith("C:")) return l.specifics[h] || undefined;
  return undefined;
}

const csvCell = (v: string) => (/[",\r\n]/.test(v) ? `"${v.replaceAll('"', '""')}"` : v);

/** Columns the template lacks but the listings fill (item specifics for this category). */
export function extraColumns(listings: Listing[], template: EbayTemplate): string[] {
  const have = new Set(template.headers);
  const extra = new Set<string>();
  for (const l of listings)
    for (const k of Object.keys(l.specifics)) if (l.specifics[k] && !have.has(k)) extra.add(k);
  return [...extra];
}

/** The upload file: template info lines, the template header (+ extra specifics), one row per listing. */
export function toEbayCsv(listings: Listing[], rules: ImportRules, template: EbayTemplate): string {
  const headers = [...template.headers, ...extraColumns(listings, template)];
  const lines = [...template.infoRows, headers].map((r) => r.map(csvCell).join(","));
  for (const l of listings) {
    lines.push(headers.map((h) => csvCell(valueFor(h, l, rules, template) ?? "")).join(","));
  }
  return `${lines.join("\r\n")}\r\n`;
}

/* ---------- supplement files (prices, photos, UPCs) ---------- */

/** RFC 4180 CSV -> rows. Handles quoted cells, doubled quotes and embedded line breaks. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  const src = text.replace(/^﻿/, "");
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') {
        field += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else field += ch;
  }
  if (field !== "" || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

/**
 * Fills prices, retail prices, photos, UPCs and ePIDs from a second table, matched
 * by SKU first and model second. A value in the supplement replaces the stock
 * sheet's, so a fresh price list wins. `unmatched` counts supplement rows that
 * matched nothing (usually a typo in a SKU).
 */
export function mergeSupplement(
  items: ImportItem[],
  rows: string[][],
): { items: ImportItem[]; matched: number; unmatched: number } {
  const hr = findHeaderRow(rows);
  const map = guessColumns(rows[hr] ?? []);
  const bySku = new Map(items.map((it, i) => [it.sku.toLowerCase(), i]));
  const byModel = new Map<string, number>();
  for (const [i, it] of items.entries()) if (it.model) byModel.set(it.model.toLowerCase(), i);
  const out = items.map((it) => ({ ...it }));
  let matched = 0;
  let unmatched = 0;
  for (const row of rows.slice(hr + 1)) {
    const sku = cell(row, map.sku).toLowerCase();
    const model = cell(row, map.model).toLowerCase();
    if (!sku && !model) continue;
    const idx = (sku ? bySku.get(sku) : undefined) ?? (model ? byModel.get(model) : undefined);
    const target = idx === undefined ? undefined : out[idx];
    if (!target) {
      unmatched++;
      continue;
    }
    matched++;
    const price = num(cell(row, map.price));
    const retail = num(cell(row, map.retail));
    if (price !== null) target.price = price;
    if (retail !== null) target.retail = retail;
    for (const k of ["upc", "epid", "photo", "description"] as const) {
      const v = cell(row, map[k]);
      if (v) target[k] = v;
    }
  }
  return { items: out, matched, unmatched };
}

/**
 * A lot listing's channel SKU is `<base SKU>-P<n>` (see buildListings). Given a SKU
 * that matched nothing directly, returns the base SKU and how many stock units one
 * sale consumes, or null when it is not a lot SKU.
 */
export function parseLotSku(sku: string): { base: string; unitsPerSale: number } | null {
  const m = /^(.+)-P(\d{1,3})$/i.exec(sku.trim());
  const n = m ? Number(m[2]) : 0;
  return m?.[1] && n >= 2 ? { base: m[1], unitsPerSale: n } : null;
}
